import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GameCard, { Game } from 'components/gameCard/gameCard';
import cx from 'classnames';
import styles from './searchBar.module.scss';
import { gamesService } from 'api/games';
import { useAppSelector } from 'store/hooks';

interface SearchBarProps {
  onClose?: () => void;
  games?: Game[];
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  disableDropdown?: boolean;
  noColoredBorder?: boolean;
  className?: string;
}

const getDynamicPageSize = (): number => {
  const width = window.innerWidth;
  if (width <= 768) {
    return 18;
  }
  const containerWidth = Math.min(width - 64, 1200);
  const colWidth = 145 + 14;
  const columnsCount = Math.floor(containerWidth / colWidth) || 1;
  return columnsCount * 3;
};

const SearchBar: React.FC<SearchBarProps> = ({
  onClose,
  placeholder,
  value: controlledValue,
  onChange: onControlledChange,
  disableDropdown = false,
  noColoredBorder = false,
  className,
}) => {
  const { t } = useTranslation();
  const config = useAppSelector((state) => state.site.config);
  const isBlocked = config?.geo?.isBlocked;

  const [internalQuery, setInternalQuery] = useState('');
  const isControlled = controlledValue !== undefined;
  const query = isControlled ? controlledValue : internalQuery;

  const setQuery = (newVal: string) => {
    if (!isControlled) {
      setInternalQuery(newVal);
    }
    if (onControlledChange) {
      onControlledChange(newVal);
    }
  };

  const [isFocused, setIsFocused] = useState(false);
  const [results, setResults] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [originalScrollY, setOriginalScrollY] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Restore scroll position and clear search when search is unfocused
  useEffect(() => {
    if (disableDropdown) return;
    if (!isFocused) {
      setQuery('');
      setResults([]);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalDocs(0);

      if (originalScrollY !== null) {
        window.scrollTo({
          top: originalScrollY,
          behavior: 'smooth',
        });
        setOriginalScrollY(null);
      }
    }
  }, [isFocused, originalScrollY, disableDropdown]);

  // Debounced API Search logic for initial query page 1
  useEffect(() => {
    if (disableDropdown) return;
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      setResults([]);
      setLoading(false);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalDocs(0);
      return;
    }

    setLoading(true);

    const timer = setTimeout(async () => {
      try {
        const pageSize = getDynamicPageSize();
        const response = await gamesService.searchGames(trimmed, 1, pageSize);
        if (response && response.data && response.data.docs) {
          const mapped = response.data.docs.map((g: any) => ({
            id: g.gameId,
            name: g.title,
            imgUrl: g.imageUrl,
            playingCount: 0,
            isFavorite: false,
          }));
          setResults(mapped);
          setTotalPages(response.data.totalPages || 1);
          setTotalDocs(response.data.totalDocs || 0);
          setCurrentPage(1);
        } else {
          setResults([]);
          setTotalPages(1);
          setTotalDocs(0);
          setCurrentPage(1);
        }
      } catch (err) {
        console.error('Failed to search games', err);
        setResults([]);
        setTotalPages(1);
        setTotalDocs(0);
        setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Prevent body scrolling when search is focused
  useEffect(() => {
    if (disableDropdown) return;
    if (isFocused) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFocused, disableDropdown]);

  // Handle clicking outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
        if (onClose) onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleClearOrClose = () => {
    if (query.length > 0) {
      setQuery('');
      setResults([]);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalDocs(0);
    } else {
      setIsFocused(false);
      if (onClose) onClose();
    }
  };

  const handleLoadMore = async () => {
    if (loadingMore || currentPage >= totalPages) return;

    setLoadingMore(true);
    const nextPage = currentPage + 1;
    const trimmed = query.trim();

    try {
      const pageSize = getDynamicPageSize();
      const response = await gamesService.searchGames(trimmed, nextPage, pageSize);
      if (response && response.data && response.data.docs) {
        const mapped = response.data.docs.map((g: any) => ({
          id: g.gameId,
          name: g.title,
          imgUrl: g.imageUrl,
          playingCount: 0,
          isFavorite: false,
        }));
        setResults((prev) => [...prev, ...mapped]);
        setCurrentPage(nextPage);
        setTotalPages(response.data.totalPages || 1);
        setTotalDocs(response.data.totalDocs || 0);
      }
    } catch (err) {
      console.error('Failed to load more search results', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleFocus = () => {
    if (isBlocked) return;
    if (disableDropdown) {
      setIsFocused(true);
      return;
    }
    if (!isFocused) {
      setOriginalScrollY(window.pageYOffset);
    }
    setIsFocused(true);
    setTimeout(() => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const absoluteTop = window.pageYOffset + rect.top;
        const offset = window.innerHeight * 0.2;
        window.scrollTo({
          top: absoluteTop - offset,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  return (
    <>
      {!disableDropdown && isFocused && <div className={styles.searchBackdrop} />}
      <div
        className={cx(
          styles.searchWrapper,
          { [styles.focused]: !disableDropdown && isFocused },
          className,
        )}
        ref={containerRef}
      >
        <div
          className={cx(styles.searchContainer, {
            [styles.focused]: isFocused,
            [styles.disabled]: isBlocked,
            [styles.noColoredBorder]: noColoredBorder,
          })}
        >
          <span className={styles.searchIcon}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </span>

          <input
            className={styles.searchInput}
            name="search"
            placeholder={placeholder || t('searchBar.placeholder', 'Search your game')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={handleFocus}
            disabled={isBlocked}
          />

          {(isFocused || query.length > 0) && (
            <button
              className={styles.clearBtn}
              onClick={handleClearOrClose}
              aria-label={t('searchBar.clearOrClose', 'Clear or close search')}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          )}
        </div>

        {/* Dropdown Results Box */}
        {!disableDropdown && isFocused && query.trim().length > 0 && (
          <div className={styles.searchResultsBox}>
            {loading ? (
              <div
                style={{
                  color: '#7a9ab0',
                  padding: '32px 0',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                {t('common.searching', 'Searching...')}
              </div>
            ) : results.length > 0 ? (
              <>
                <div className={styles.resultsGrid}>
                  {results.map((game, idx) => (
                    <div key={`${game.id}-${idx}`} className={styles.gridItem}>
                      <GameCard game={game} />
                    </div>
                  ))}
                </div>

                <div className={styles.loadMoreContainer}>
                  <div className={styles.viewedStats}>
                    {t('common.viewedStats', {
                      count: results.length,
                      total: totalDocs,
                      defaultValue: 'You viewed: {{count}} out of {{total}} Games',
                    })}
                  </div>
                  {currentPage < totalPages && (
                    <button
                      className={styles.loadMoreBtn}
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore
                        ? t('common.loading', 'Loading...')
                        : t('common.loadMore', 'Load more')}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div
                style={{
                  color: '#7a9ab0',
                  padding: '32px 0',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                {t('common.noResultsFound', 'No results found.')}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SearchBar;
